import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { usePageTitle } from "@/contexts/PageTitleContext";
import { useTenant } from "@/contexts/TenantContext";
import { buildTenantPath } from "@/lib/tenantPath";
import { queryKeys } from "@/lib/queryClient";
import {
  useAppointmentsList,
  useAppointmentFilters,
} from "@/hooks/useAppointments";

import AllAppointmentsCard from "@/components/appointments/AllAppointmentsCard";
import AppointmentCustomViewsDialog from "@/components/appointments/AppointmentCustomViewsDialog";

export default function AppointmentsPage() {
  usePageTitle("Appointments");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const { selectedTenantId } = useTenant();
  
  const buildPath = useCallback((basePath) => {
    return buildTenantPath(basePath, selectedTenantId);
  }, [selectedTenantId]);

  // Filter fields fetched from API
  const [runFilterFields, setRunFilterFields] = useState([]);
  const [runFilterOptions, setRunFilterOptions] = useState({});

  // all appointments list controls (Zoho-style table)
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("desc");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(new Set());

  const [appointmentsPrefs, setAppointmentsPrefs] = useState({
    visible_filters: ["start_at", "status", "location", "custom_filters"],
    custom_filters: [],
    custom_views: [],
    active_custom_view_id: "all",
    table_columns: ["status", "full_name", "start_at", "end_at", "location", "phone_number", "state"],
    all_table_columns: ["status", "full_name", "start_at", "end_at", "location", "phone_number", "state"],
    page_size: 10,
  });

  const [prefsReady, setPrefsReady] = useState(false);
  const pageSizeInitRef = useRef(false);

  const visibleFilters = appointmentsPrefs.visible_filters;
  const customFilters = appointmentsPrefs.custom_filters;
  const customViews = appointmentsPrefs.custom_views;
  const activeCustomViewId = appointmentsPrefs.active_custom_view_id;

  const allTableColumns = Array.isArray(appointmentsPrefs.all_table_columns) && appointmentsPrefs.all_table_columns.length > 0
    ? appointmentsPrefs.all_table_columns
    : appointmentsPrefs.table_columns;
  const tableColumns = activeCustomViewId === "all" ? allTableColumns : appointmentsPrefs.table_columns;

  const activeViewLabel = useMemo(() => {
    if (activeCustomViewId === "all") return "All Appointments";
    const v = (customViews || []).find((x) => x.id === activeCustomViewId);
    return v?.name || "All Appointments";
  }, [activeCustomViewId, customViews]);

  const [filtersDraft, setFiltersDraft] = useState({});
  const [filtersApplied, setFiltersApplied] = useState({});
  const [appointmentsFiltersOpen, setAppointmentsFiltersOpen] = useState(() => {
    try {
      const stored = localStorage.getItem("appointments_filters_open");
      return stored ? stored === "true" : true;
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("appointments_filters_open", String(appointmentsFiltersOpen));
    } catch {
      // ignore
    }
  }, [appointmentsFiltersOpen]);

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const setPrefsPatch = async (patch) => {
    const next = {
      ...appointmentsPrefs,
      ...patch,
    };
    setAppointmentsPrefs(next);
    await api.put("/user-prefs", { appointments: next });
  };

  const applySort = (field, dir) => {
    setSortBy(field);
    setSortDir(dir);
    setOffset(0);
  };

  const clearSort = () => {
    setSortBy(null);
    setOffset(0);
  };

  const toggleAll = (checked) => {
    if (!checked) {
      setSelected(new Set());
      return;
    }
    setSelected(new Set((items || []).map((x) => x.id)));
  };

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const [customViewsOpen, setCustomViewsOpen] = useState(false);

  const getActiveView = () => {
    if (activeCustomViewId === "all") return { id: "all", name: "All Appointments", filters_applied: {}, columns: allTableColumns };
    return (customViews || []).find((v) => v.id === activeCustomViewId) || { id: "all", name: "All Appointments", filters_applied: {}, columns: allTableColumns };
  };

  const applyView = async (v) => {
    if (!v || !v.id) return;
    if (v.id === "all") {
      setFiltersApplied({});
      setFiltersDraft({});
      const allColumns = Array.isArray(appointmentsPrefs.all_table_columns) && appointmentsPrefs.all_table_columns.length > 0
        ? appointmentsPrefs.all_table_columns
        : appointmentsPrefs.table_columns;
      await setPrefsPatch({ active_custom_view_id: "all", table_columns: allColumns });
      setOffset(0);
      return;
    }

    setFiltersApplied(v.filters_applied || {});
    setFiltersDraft(v.filters_applied || {});
    if (Array.isArray(v.columns) && v.columns.length > 0) {
      await setPrefsPatch({ active_custom_view_id: v.id, table_columns: v.columns });
    } else {
      await setPrefsPatch({ active_custom_view_id: v.id });
    }
    setOffset(0);
  };

  // ===== REACT QUERY HOOKS =====
  
  // Appointments list query - only enabled when prefs are ready
  const appointmentsQuery = useAppointmentsList({
    filters: filtersApplied,
    search: qDebounced,
    page: Math.floor(offset / limit),
    pageSize: limit,
    sortBy,
    sortDir,
    enabled: prefsReady,
  });
  
  // Filter options query
  const filtersQuery = useAppointmentFilters();

  // Sync React Query data to local state for component compatibility
  useEffect(() => {
    if (appointmentsQuery.data) {
      setItems(appointmentsQuery.data.items || []);
      setTotal(appointmentsQuery.data.total || 0);
    }
  }, [appointmentsQuery.data]);

  // Derive filter options from query
  useEffect(() => {
    if (filtersQuery.data) {
      setRunFilterFields(filtersQuery.data.fields || []);
      setRunFilterOptions(filtersQuery.data.optionsByField || {});
    }
  }, [filtersQuery.data]);

  // Combined loading state
  const loading = !prefsReady || appointmentsQuery.isLoading;

  // Function to invalidate appointments queries (for refetch after mutations)
  const invalidateAppointments = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.appointments.all(selectedTenantId),
    });
  }, [queryClient, selectedTenantId]);

  // Load prefs and derive filters BEFORE allowing fetch
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/user-prefs");
        const next = res.data?.appointments || {};
        
        const activeId = typeof next.active_custom_view_id === "string" ? next.active_custom_view_id : "all";
        const customViewsList = Array.isArray(next.custom_views) ? next.custom_views : [];
        
        let derivedFilters = {};
        if (activeId !== "all") {
          const activeView = customViewsList.find(v => v.id === activeId);
          if (activeView && activeView.filters_applied) {
            derivedFilters = activeView.filters_applied;
          }
        }
        
        setFiltersApplied(derivedFilters);
        setFiltersDraft(derivedFilters);
        
        setAppointmentsPrefs((prev) => {
          const allColumns = Array.isArray(next.all_table_columns) && next.all_table_columns.length > 0
            ? next.all_table_columns
            : Array.isArray(next.table_columns) && next.table_columns.length > 0
              ? next.table_columns
              : prev.all_table_columns;
          const tableCols = Array.isArray(next.table_columns) && next.table_columns.length > 0 ? next.table_columns : prev.table_columns;
          const merged = {
            ...prev,
            ...next,
            visible_filters: Array.isArray(next.visible_filters) ? next.visible_filters : prev.visible_filters,
            custom_filters: Array.isArray(next.custom_filters) ? next.custom_filters : prev.custom_filters,
            table_columns: activeId === "all" ? allColumns : tableCols,
            all_table_columns: allColumns,
            custom_views: customViewsList,
            active_custom_view_id: activeId,
            page_size: typeof next.page_size === "number" ? next.page_size : prev.page_size,
          };
          if (!pageSizeInitRef.current) {
            setLimit(merged.page_size || prev.page_size || 10);
            pageSizeInitRef.current = true;
          }
          return merged;
        });
        
        setPrefsReady(true);
      } catch {
        setPrefsReady(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col h-[calc(100vh-230px)] p-6 gap-4" data-testid="appointments-page">
      <AllAppointmentsCard
        items={items}
        total={total}
        loading={loading}
        q={q}
        onChangeQ={setQ}
        tableColumns={tableColumns}
        onChangeTableColumns={async (cols) => {
          const patch = { table_columns: cols };
          if (activeCustomViewId === "all") {
            patch.all_table_columns = cols;
          }
          await setPrefsPatch(patch);
        }}
        limit={limit}
        onChangeLimit={(n) => {
          setLimit(n);
          setOffset(0);
          setPrefsPatch({ page_size: n });
        }}
        offset={offset}
        onChangeOffset={setOffset}
        sortBy={sortBy}
        sortDir={sortDir}
        onApplySort={applySort}
        onClearSort={clearSort}
        selected={selected}
        onToggleAll={toggleAll}
        onToggleRow={toggleRow}
        onOpenRow={(appt) => navigate(buildPath(`/app/appointments/${appt.id}`))}
        filterOpen={appointmentsFiltersOpen}
        onToggleFilterOpen={() => setAppointmentsFiltersOpen((v) => !v)}
        viewLabel={activeViewLabel}
        views={customViews}
        onSelectView={applyView}
        onManageViews={() => setCustomViewsOpen(true)}
        visibleFilters={visibleFilters}
        filtersDraft={filtersDraft}
        onChangeFiltersDraft={setFiltersDraft}
        filtersApplied={filtersApplied}
        filterFields={runFilterFields}
        filterOptionsByField={runFilterOptions}
        onApplyFilters={(draft) => {
          setFiltersApplied(draft);
          setOffset(0);
        }}
        onClearFilters={() => {
          setFiltersDraft({});
          setFiltersApplied({});
          setOffset(0);
        }}
        onDeleteSelected={async () => {
          try {
            const ids = Array.from(selected);
            await Promise.all(ids.map((id) => api.delete(`/appointments/${id}`)));
            setSelected(new Set());
            invalidateAppointments();
          } catch (e) {
            // ignore
          }
        }}
      />

      <AppointmentCustomViewsDialog
        open={customViewsOpen}
        onOpenChange={setCustomViewsOpen}
        value={customViews}
        currentDraft={{ filters_applied: filtersApplied }}
        currentColumns={tableColumns}
        onSave={async (views) => {
          await setPrefsPatch({ custom_views: views });
        }}
      />
    </div>
  );
}
